/**
 * Image Metadata and Structured Data for Google Image Search
 * All images optimized for "Randiv Dulneth" searches
 */

export const imageMetadata = {
  homeProfile: {
    url: 'https://mr-randiv.vercel.app/images/Homeprofile-800x800.webp',
    alt: 'Randiv Dulneth (MrRandiv) - Software Engineer & SLIIT Student | Full-Stack Developer Portfolio',
    title: 'Randiv Dulneth - Software Engineer',
    caption: 'Randiv Dulneth (MrRandiv) - Software Engineer and SLIIT Student. Full-Stack Developer specializing in MERN Stack, React, Node.js. Professional portfolio photo of Dulneth Randiv.',
    description: 'Professional profile photo of Randiv Dulneth (MrRandiv), a Software Engineer and undergraduate student at SLIIT (Sri Lanka Institute of Information Technology) specializing in ICT. Based in Kandy, Sri Lanka.',
    keywords: 'Randiv Dulneth, MrRandiv, Dulneth Randiv, SLIIT Student, Software Engineer, Full-Stack Developer, Kandy Sri Lanka',
    author: 'Randiv Dulneth',
    location: 'Kandy, Sri Lanka',
    width: 753,
    height: 1004,
  },
  aboutHero: {
    url: 'https://mr-randiv.vercel.app/images/Abouthero1.webp',
    alt: 'Randiv Dulneth (MrRandiv) - About Page Hero Banner | SLIIT Software Engineer Portfolio',
    title: 'Randiv Dulneth - About Me',
    caption: 'Randiv Dulneth (MrRandiv) About Page Hero Banner. SLIIT Software Engineer and ICT student. Learn about Dulneth Randiv\'s journey as a Full-Stack Developer in Kandy, Sri Lanka.',
    description: 'About page hero banner for Randiv Dulneth (MrRandiv), showcasing the journey of a SLIIT student and Software Engineer specializing in Full-Stack Development and MERN Stack technologies.',
    keywords: 'Randiv Dulneth, MrRandiv, About Randiv, SLIIT, Software Engineer, ICT Student, Dulneth Randiv',
    author: 'Randiv Dulneth',
    location: 'Kandy, Sri Lanka',
    width: 1920,
    height: 1080,
  },
  logo512: {
    url: 'https://mr-randiv.vercel.app/logo-512.png',
    alt: 'Randiv Dulneth (MrRandiv) Logo - Software Engineer & SLIIT Student',
    title: 'Randiv Dulneth - Official Logo',
    caption: 'Randiv Dulneth (MrRandiv) Logo - Official brand logo for Software Engineer and SLIIT student Dulneth Randiv. Portfolio branding for Full-Stack Developer.',
    description: 'Official logo of Randiv Dulneth (MrRandiv), Software Engineer and SLIIT student. Brand identity for Full-Stack Developer portfolio.',
    keywords: 'Randiv Dulneth Logo, MrRandiv Brand, Software Engineer Logo, SLIIT Student',
    author: 'Randiv Dulneth',
    location: 'Kandy, Sri Lanka',
    width: 512,
    height: 512,
  },
  logo192: {
    url: 'https://mr-randiv.vercel.app/logo-192.png',
    alt: 'Randiv Dulneth (MrRandiv) Icon - Software Engineer Portfolio',
    title: 'Randiv Dulneth - Logo Icon',
    caption: 'Randiv Dulneth (MrRandiv) Small Logo - Brand icon for Software Engineer portfolio. SLIIT student Dulneth Randiv professional branding.',
    description: 'Small logo icon of Randiv Dulneth (MrRandiv), representing Software Engineer and SLIIT student brand identity.',
    keywords: 'Randiv Dulneth Icon, MrRandiv Logo, Portfolio Icon',
    author: 'Randiv Dulneth',
    location: 'Kandy, Sri Lanka',
    width: 192,
    height: 192,
  },
};

/**
 * Generate ImageObject structured data for a specific image
 */
export const generateImageSchema = (imageKey) => {
  const image = imageMetadata[imageKey];
  if (!image) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'ImageObject',
    contentUrl: image.url,
    url: image.url,
    name: image.title,
    description: image.description,
    caption: image.caption,
    author: {
      '@type': 'Person',
      name: image.author,
      alternateName: 'MrRandiv',
    },
    creator: {
      '@type': 'Person',
      name: 'Randiv Dulneth',
      alternateName: ['MrRandiv', 'Dulneth Randiv', 'Dulneth'],
    },
    copyrightHolder: {
      '@type': 'Person',
      name: 'Randiv Dulneth',
    },
    copyrightNotice: '© 2025 Randiv Dulneth (MrRandiv). All rights reserved.',
    creditText: 'Randiv Dulneth (MrRandiv)',
    acquireLicensePage: 'https://mr-randiv.vercel.app/contact',
    license: 'https://mr-randiv.vercel.app/',
    contentLocation: {
      '@type': 'Place',
      name: image.location,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Kandy',
        addressRegion: 'Central Province',
        addressCountry: 'LK',
      },
    },
    keywords: image.keywords,
    width: {
      '@type': 'QuantitativeValue',
      value: image.width,
      unitText: 'px',
    },
    height: {
      '@type': 'QuantitativeValue',
      value: image.height,
      unitText: 'px',
    },
    encodingFormat: image.url.endsWith('.webp') ? 'image/webp' : (image.url.endsWith('.png') ? 'image/png' : 'image/jpeg'),
    uploadDate: '2025-10-20',
    datePublished: '2025-10-20',
  };
};

/**
 * Generate combined ImageGallery structured data for all images
 */
export const generateImageGallerySchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    name: 'Randiv Dulneth (MrRandiv) Portfolio Images',
    description: 'Image gallery of Randiv Dulneth (MrRandiv), Software Engineer and SLIIT student. Portfolio photos, project banners, and professional images of Dulneth Randiv.',
    author: {
      '@type': 'Person',
      name: 'Randiv Dulneth',
      alternateName: ['MrRandiv', 'Dulneth Randiv', 'Dulneth'],
    },
    image: Object.keys(imageMetadata).map(key => imageMetadata[key].url),
    associatedMedia: Object.keys(imageMetadata).map(key => ({
      '@type': 'ImageObject',
      contentUrl: imageMetadata[key].url,
      name: imageMetadata[key].title,
      description: imageMetadata[key].description,
      caption: imageMetadata[key].caption,
    })),
  };
};

/**
 * Generate Person schema with all images
 */
export const generatePersonWithImagesSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Randiv Dulneth',
    alternateName: ['MrRandiv', 'Mr Randiv', 'Dulneth', 'Dulneth Randiv', 'Randiv'],
    url: 'https://mr-randiv.vercel.app/',
    image: [
      imageMetadata.homeProfile.url,
      imageMetadata.aboutHero.url,
      imageMetadata.logo512.url,
    ],
    photo: imageMetadata.homeProfile.url,
    logo: imageMetadata.logo512.url,
    jobTitle: 'Software Engineer',
    description: 'Randiv Dulneth (MrRandiv, Dulneth Randiv) is a Software Engineer and SLIIT student specializing in Full-Stack Development, MERN Stack, React, Node.js. Based in Kandy, Sri Lanka.',
    worksFor: {
      '@type': 'EducationalOrganization',
      name: 'Sri Lanka Institute of Information Technology',
      alternateName: 'SLIIT',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Kandy',
      addressRegion: 'Central Province',
      addressCountry: 'LK',
    },
    sameAs: [
      'https://github.com/Randiv01',
      'https://linkedin.com/in/randiv-dulneth',
      'https://twitter.com/mrrandiv',
      'https://www.instagram.com/randiv_xi',
    ],
  };
};

export default imageMetadata;
